import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// Redux actions
import {
  fetchProductList,
  setEventName,
  setPagination,
} from "../../../Reducer/dataAdminSlice";

// Product batches slice
import { fetchPendingBatches } from "../../../Reducer/productBatchesSlice";

// MUI components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Modal,
  IconButton,
  Button,
  useTheme,
  styled,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

// Custom components
import Row from "./Row";
import Filters from "./Filters";
import UploadCsv from "./UploadCsv";
import DeficiateAlerts from "./DeficiateAlerts";
import ManualBatchForm from "./ManualBatchForm";

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: theme.shadows[2],
  margin: theme.spacing(3, 0),
  overflow: "hidden",
  width: "100%",
  maxWidth: "800px",
}));

const StyledTableHead = styled(TableHead)(() => ({
  background: "#1976d2",
  "& .MuiTableCell-root": {
    textAlign: "left",
    verticalAlign: "middle",
    padding: "10px 12px",
    fontWeight: 600,
    color: "#fff",
    fontSize: "0.875rem",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:hover": { backgroundColor: "#1976d2" },
  "&:last-child td, &:last-child th": { border: 0 },
}));





const UploadedProductList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { data, pagination, eventName = "", loading, error } = useSelector(
    (state) => state.dataAdmin
  );

  // Use productBatches slice
  const { data: batchesData, loading: batchesLoading, error: batchesError } = useSelector(
    (state) => state.productBatches
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [manualBatchModalOpen, setManualBatchModalOpen] = useState(false);

  useEffect(() => {
    dispatch(
      fetchProductList({
        page: pagination.current_page,
        per_page: pagination.per_page,
        event_name: eventName,
      })
    );
  }, [dispatch, pagination.current_page, pagination.per_page, eventName]);

  // ✅ fetch batches for DeficiateAlerts using Redux slice
  useEffect(() => {
    dispatch(fetchPendingBatches());
  }, [dispatch]);

  const handleActionClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 600, color: "#1976d2" }}
        >
          Product Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setManualBatchModalOpen(true)}
          sx={{
            borderRadius: '8px',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.85rem',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 3px 12px rgba(25,118,210,0.25)',
            '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)' },
          }}
        >
          Create Manual Batch
        </Button>
      </Box>

      <Filters onFilterChange={(value) => dispatch(setEventName(value))} />

      {/* Table with right sidebar for red chips */}
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {/* Table */}
        <StyledTableContainer component={Paper}>
          <Table aria-label="product table" size="small">
            <StyledTableHead>
              <StyledTableRow>
                <TableCell />
                <TableCell>Event Name</TableCell>
                <TableCell>Actions</TableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ py: 4, color: "error.main" }}
                  >
                    <Typography variant="body1">Error: {error}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data &&
                data.map((row, i) => (
                  <Row key={i} row={row} onActionClick={handleActionClick} />
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Right chips column */}
        <Box>
          <Typography
            variant="h2"
            fontWeight="700"
            sx={{
              ml: 5,
              mb: -2,
              color: "#1976d2",
              fontSize: "1.5rem",
              fontFamily: "roboto,Arial, sans-serif",
            }}
          >
            Deficiate Alerts List :
          </Typography>
          {/* ✅ Now uses live API data */}
          <DeficiateAlerts batchesData={batchesData} loading={batchesLoading} error={batchesError} />
        </Box>
      </Box>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Paper elevation={2} sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={Number(pagination.total_count) || 0}
            rowsPerPage={pagination.per_page}
            page={pagination.current_page - 1}
            onPageChange={(event, newPage) =>
              dispatch(setPagination({ current_page: newPage + 1 }))
            }
            onRowsPerPageChange={(event) =>
              dispatch(
                setPagination({
                  current_page: 1,
                  per_page: parseInt(event.target.value, 10),
                })
              )
            }
            sx={{
              "& .MuiTablePagination-toolbar": { padding: theme.spacing(0, 2) },
            }}
          />
        </Paper>
      </Box>

      {/* Action Modal */}
      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            border: "2px solid #3c6f85ff",
            borderRadius: "12px",
          }}
        >
          <IconButton
            onClick={handleModalClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedRow && (
            <UploadCsv
              woocommerce_product_id={selectedRow.woocommerce_product_id_int || selectedRow.product_variant_id}
              event_name={selectedRow.event_name}
            />
          )}
        </Box>
      </Modal>

      {/* Manual Batch Modal */}
      <Modal open={manualBatchModalOpen} onClose={() => setManualBatchModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
        >
          <IconButton
            onClick={() => setManualBatchModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <ManualBatchForm
            onSuccess={() => {
              setManualBatchModalOpen(false);
              dispatch(fetchPendingBatches());
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default UploadedProductList;
